import React from "react";
import { Layout } from "lucide-react";
import type { ContentAreaComponent as ContentAreaComponentType } from "../schemas/validation";

interface ContentAreaComponentProps {
  component: ContentAreaComponentType;
  isEditing?: boolean;
}

/**
 * ContentAreaComponent - A placeholder component used in page layouts
 *
 * This component marks where page-specific content will be rendered when
 * a layout is applied to a page. In the layout editor, it shows a visual
 * placeholder. When rendering actual pages, this component is replaced
 * with the page's actual components.
 */
export const ContentAreaComponent: React.FC<ContentAreaComponentProps> = ({
  component,
  isEditing = false,
}) => {
  const { data } = component;

  // Get styling values with defaults
  const minHeight = data.minHeight || 200;
  const backgroundColor = data.backgroundColor || "transparent";
  const label = data.label || "Page Content";
  const maxWidth = data.maxWidth || "container";
  const centered = data.centered !== false;
  const padding = data.padding || { top: 0, bottom: 0, left: 0, right: 0 };

  // Max width classes
  const maxWidthMap = {
    full: "w-full",
    container: "max-w-7xl",
    narrow: "max-w-4xl",
  };

  const maxWidthClass = maxWidthMap[maxWidth] || maxWidthMap.container;

  // In editing mode (layout editor), show a visual placeholder
  if (isEditing) {
    return (
      <div
        className={`${maxWidthClass} ${centered ? "mx-auto" : ""}`}
        style={{
          minHeight: `${minHeight}px`,
          backgroundColor: backgroundColor || "#f3f4f6",
          paddingTop: `${padding.top}px`,
          paddingBottom: `${padding.bottom}px`,
          paddingLeft: `${padding.left}px`,
          paddingRight: `${padding.right}px`,
        }}
      >
        <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-blue-300 rounded-lg p-8 bg-blue-50/50">
          <Layout className="w-12 h-12 text-blue-400 mb-4" />
          <h3 className="text-lg font-medium text-blue-700">{label}</h3>
          <p className="text-sm text-blue-500 mt-2 text-center max-w-md">
            Page-specific content will appear here when this layout is applied to a page.
          </p>
        </div>
      </div>
    );
  }

  // In preview/render mode, the content-area is just a container
  // The actual page content will be injected by the rendering logic
  return (
    <div
      className={`content-area-placeholder ${maxWidthClass} ${centered ? "mx-auto" : ""}`}
      style={{
        minHeight: `${minHeight}px`,
        backgroundColor,
        paddingTop: `${padding.top}px`,
        paddingBottom: `${padding.bottom}px`,
        paddingLeft: `${padding.left}px`,
        paddingRight: `${padding.right}px`,
      }}
      data-content-area="true"
      data-component-id={component.id}
    >
      {/* Content will be injected here by the layout rendering system */}
    </div>
  );
};
